import * as yup from 'yup';
import { Dayjs } from 'dayjs';

const caseProcedureSchema = yup.object({
    date_of_procedure: yup
        .mixed<Dayjs>()
        .required('Date of procedure is required'),
    procedure_name: yup.string().required('Procedure name is required').defined(),
    type: yup.string().required('Procedure type is required'),
    approach: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Approach must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    position: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Position must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    indication: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Indication must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    minimally_invasive: yup.string().defined(),
    navigation: yup.string().defined(),
    robotic: yup.string().defined(),
    side: yup.string().defined(),
    target: yup.string().defined(),
    nerve: yup.string().defined(),
    location: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Location must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    _procedure: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Procedure must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    tici_grade: yup.string().defined(),
    osteotomy: yup.string().defined(),
    _fusion: yup.string().defined(),
    interbody_fusion: yup.string().defined(),
    extension_to_pelvis: yup.string().defined(),
    fusion_levels: yup.string().defined(),
    decompression_levels: yup.string().defined(),
    morphogenic_protein: yup.string().defined(),
    bone_graft: yup.string().defined(),
    implant: yup.string().defined(),
    implant_type: yup.string().defined(),
    _vendors: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Vendors must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    vendors: yup.string().defined(),
    access: yup.string().defined(),
    vascular_closure_device: yup.string().defined(),
    duration_of_radiation: yup.string().defined(),
    duration_of_procedure: yup.string().defined(),
    findings: yup.string().defined(),
    complications: yup.string().defined(),
    outcome: yup.string().defined(),
    cpts: yup
        .array()
        .of(
            yup.object({
                id: yup.string().optional(),
                code: yup.string().required(),
                value: yup.string().required(),
                rvu_value: yup.number().required(),
                quantity: yup.number().min(1).defined(),
            })
        )
        .defined(),
    cpt_modifier: yup
        .object({
            id: yup.string().required(),
            description: yup.string().required(),
            percentage: yup.number().required(),
        })
        .nullable(),
    cpt_modifier_id: yup.string().nullable().defined(),

});

export const addCaseSchema = yup.object({
    patient_first_name: yup.string().required('First name is required'),
    patient_last_name: yup.string().required('Last name is required'),
    gender: yup.string().required('Gender is required'),
    date_of_birth: yup.mixed<Dayjs>().nullable().defined(),
    age: yup.string().defined(),
    mrn: yup.string().defined(),
    race: yup.string().defined(),
    insurance: yup.string().defined(),
    insurance_status: yup.string().defined(),
    hospital_id: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
        )
        .required('Institution is required'),
    primary_surgeons: yup
        .array()
        .of(
            yup.object({
                id: yup.string().optional(),
                invitee_name: yup.string().required(),
            })
        )
        .defined(),
    co_surgeons: yup
        .array()
        .of(
            yup.object({
                id: yup.string().optional(),
                invitee_name: yup.string().required(),
            })
        )
        .defined(),
    procedure_diagnosis: yup.string().defined(),
    icds: yup
        .array()
        .of(
            yup.object({
                id: yup.string().optional(),
                code: yup.string().required('ICD code is required'),
                value: yup.string().required('ICD value is required'),
            })
        )
        .defined(),
    icd_id: yup
        .array()
        .of(yup.string().required())
        .defined(),
    date_of_procedure: yup
        .mixed<Dayjs>()
        .required('Date of procedure is required'),
    procedure_name: yup.string().required('Procedure name is required').defined(),
    type: yup.string().required('Procedure type is required'),
    approach: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Approach must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    position: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Position must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    indication: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Indication must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    minimally_invasive: yup.string().defined(),
    navigation: yup.string().defined(),
    robotic: yup.string().defined(),
    side: yup.string().defined(),
    target: yup.string().defined(),
    nerve: yup.string().defined(),
    location: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Location must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    _procedure: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Procedure must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    tici_grade: yup.string().defined(),
    osteotomy: yup.string().defined(),
    _fusion: yup.string().defined(),
    interbody_fusion: yup.string().defined(),
    extension_to_pelvis: yup.string().defined(),
    fusion_levels: yup.string().defined(),
    decompression_levels: yup.string().defined(),
    morphogenic_protein: yup.string().defined(),
    bone_graft: yup.string().defined(),
    implant: yup.string().defined(),
    implant_type: yup.string().defined(),
    _vendors: yup
        .mixed<string | string[]>()
        .test(
            'string-or-string-array',
            'Vendors must be a string or an array of strings',
            (value) =>
                typeof value === 'string' ||
                (Array.isArray(value) && value.every(v => typeof v === 'string'))
        )
        .defined(),
    vendors: yup.string().defined(),
    access: yup.string().defined(),
    vascular_closure_device: yup.string().defined(),
    duration_of_radiation: yup.string().defined(),
    duration_of_procedure: yup.string().defined(),
    findings: yup.string().defined(),
    complications: yup.string().defined(),
    outcome: yup.string().defined(),
    cpts: yup
        .array()
        .of(
            yup.object({
                id: yup.string().optional(),
                code: yup.string().required(),
                value: yup.string().required(),
                rvu_value: yup.number().required(),
                quantity: yup.number().min(1).defined(),
            })
        )
        .defined(),
    cpt_modifier: yup
        .object({
            id: yup.string().required(),
            description: yup.string().required(),
            percentage: yup.number().required(),
        })
        .nullable(),
    cpt_modifier_id: yup.string().nullable().defined(),
    notes: yup.array().of(yup.string().defined()).defined(),
    case_procedures: yup.array().of(caseProcedureSchema).required().defined(),
});
